import React, { useMemo } from 'react';

import AccordionContext from './Context';
import * as S from './styles';
import { IAccordionRefObject, IAccordionProps } from './types';

export const Accordion = React.forwardRef<IAccordionRefObject, IAccordionProps>(
  (props, ref) => {
    const [expanded, setExpanded] = React.useState<number[]>(
      props.expanded ?? []
    );
    const [keys, setKeys] = React.useState<number[]>([]);

    const handleAllState = (state: boolean) => {
      if (state) {
        const items: number[] = [];
        keys.forEach(
          (key) =>
            !(props.disabled && props.disabled.includes(key)) && items.push(key)
        );
        setExpanded(items);
      } else {
        setExpanded([]);
      }
    };

    const handleState = (state: boolean, uniqueKey: number) => {
      const index = expanded.indexOf(uniqueKey);
      const newKeys = [...expanded];
      if (index > -1 && !state) {
        newKeys.splice(index, 1);
      }
      if (index < 0 && state) {
        newKeys.push(uniqueKey);
      }
      setExpanded(newKeys);
    };

    const handleToggle = (uniqueKey: number) => {
      if (!props.disabled?.includes(uniqueKey)) {
        const index = expanded.indexOf(uniqueKey);
        if (props.multi) {
          const newKeys = [...expanded];
          if (index > -1) {
            newKeys.splice(index, 1);
          } else {
            newKeys.push(uniqueKey);
          }
          setExpanded(newKeys);
        } else if (index > -1) {
          setExpanded([]);
        } else {
          setExpanded([uniqueKey]);
        }
      }
    };

    React.useImperativeHandle(
      ref,
      () => ({
        openAll() {
          handleAllState(true);
        },
        closeAll() {
          handleAllState(false);
        },
        open(uniqueKey: number) {
          handleState(true, uniqueKey);
        },
        close(uniqueKey: number) {
          handleState(false, uniqueKey);
        },
        toggle(uniqueKey: number) {
          handleToggle(uniqueKey);
        },
      }),
      [expanded, keys, props.disabled]
    );

    const ProviderValue = useMemo(
      () => ({
        expanded,
        setKeys,
        keys,
        handleToggle,
        disabled: props.disabled ?? [],
      }),
      [expanded, setKeys, keys, handleToggle, props.disabled]
    );

    return (
      <AccordionContext.Provider value={ProviderValue}>
        <S.Accordion className={props.className}>{props.children}</S.Accordion>
      </AccordionContext.Provider>
    );
  }
);

Accordion.displayName = 'Accordion';
